import type { BoxFavoriteListResponse } from "@loot-os/api-contract";
import { toast } from "@loot-os/ui/components/sonner";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import { lootFetch, qk } from "./queries.js";
import { useShopper } from "./shopper.js";

/** GET /v1/me/favorites: the boxes the signed-in shopper saved, newest first. */
export const favoritesQueryOptions = () =>
  queryOptions({
    queryKey: qk.favorites,
    queryFn: () => lootFetch<BoxFavoriteListResponse>("/me/favorites"),
    retry: false,
    staleTime: 60_000,
  });

/**
 * The shopper's saved boxes and the heart that saves or removes one. The heart moves at once and
 * rolls back if the save fails; signed out, it asks the shopper to sign in instead.
 */
export function useFavorites() {
  const { user, sessionToken } = useShopper();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The route needs the session token, minted only after the sign-in check answers.
  const query = useQuery({ ...favoritesQueryOptions(), enabled: Boolean(sessionToken) });
  // Checked here, not only by the query: a disabled query keeps the last session's cached list.
  const data = user ? query.data : undefined;
  const saved = useMemo(() => new Set((data?.data ?? []).map((f) => f.boxId)), [data]);

  const { mutate } = useMutation({
    mutationFn: ({ boxId, save }: { boxId: string; save: boolean }) =>
      lootFetch<unknown>(`/me/favorites/${encodeURIComponent(boxId)}`, {
        method: save ? "PUT" : "DELETE",
      }),
    onMutate: async ({ boxId, save }) => {
      await queryClient.cancelQueries({ queryKey: qk.favorites });
      const previous = queryClient.getQueryData<BoxFavoriteListResponse>(qk.favorites);
      const others = (previous?.data ?? []).filter((f) => f.boxId !== boxId);
      queryClient.setQueryData<BoxFavoriteListResponse>(qk.favorites, {
        data: save ? [{ boxId, createdAt: new Date().toISOString() }, ...others] : others,
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(qk.favorites, context?.previous);
      toast.error("Your favorites did not save. Try again.");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.favorites }),
  });

  const toggle = useCallback(
    (boxId: string) => {
      if (!user) {
        void navigate({ to: "/login" });
        return;
      }
      mutate({ boxId, save: !saved.has(boxId) });
    },
    [user, navigate, mutate, saved],
  );

  return { saved, signedIn: Boolean(user), loaded: !user || query.isSuccess, toggle };
}
